(() => {
  // src/lib/compare-at-price.ts
  var computeCompareAtDiscount = (compareAtCents, priceCents) => {
    if (compareAtCents <= 0 || compareAtCents <= priceCents) return 0;
    return Math.round((compareAtCents - priceCents) / compareAtCents * 100);
  };
  var buildCompareAtProperties = (compareAtCents, priceCents) => ({
    _compare_at_price: String(compareAtCents || 0),
    _compare_at_price_discount: String(computeCompareAtDiscount(compareAtCents, priceCents))
  });
  var syncCompareAtInputs = (container, compareAtCents, priceCents) => {
    const capInput = container.querySelector(
      "[data-compare-at-price-input]"
    );
    const discInput = container.querySelector(
      "[data-compare-at-price-discount-input]"
    );
    if (capInput) capInput.value = String(compareAtCents || 0);
    if (discInput)
      discInput.value = String(computeCompareAtDiscount(compareAtCents, priceCents));
  };

  // src/product-purchase-panel.ts
  (() => {
    const {
      formatMoney,
      attrInt,
      getDiscountedPrice,
      makeAnimateImageTransition,
      initSubscribeToggle,
      initQuantityChange,
      initVariantPlanSync
    } = window.HotelCollection.PurchasePanelCore;
    const ANIM_PREFIX = "ppp";
    const SUBSCRIBE_CLASS = "product-purchase-panel__btn--subscribe";
    const ADD_TO_CART_CLASS = "product-purchase-panel__btn--add-to-cart";
    const TOGGLE_BTN_ACTIVE_CLASS = "product-purchase-panel__toggle-btn--active";
    const PILL_RIGHT_CLASS = "product-purchase-panel__toggle-pill--right";
    const COLLAPSIBLE_HIDDEN_CLASS = "ppp-collapsible--hidden";
    const generateBundleId = () =>
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    const isCombinedPriceEnabled = (section) => {
      const priceRoot = section.querySelector("[data-product-price]");
      return priceRoot?.dataset.combinedPriceEnabled === "true";
    };
    const storeProductPriceData = (priceRoot, variant) => {
      if (!priceRoot || !variant) return;
      priceRoot.dataset.productPriceCents = String(Number(variant.price) || 0);
      priceRoot.dataset.productCompareAtCents = String(Number(variant.compare_at_price) || 0);
    };
    const applyPriceDisplay = (priceRoot, priceCents, compareAtCents, formatMoneyFn) => {
      if (!priceRoot) return;

      const currentWrap = priceRoot.querySelector(".price__current");
      const currentEl = priceRoot.querySelector(".price__value");
      const compareWrap = priceRoot.querySelector(".price__compare");
      const compareEl = priceRoot.querySelector(".price__compare-value");
      if (!currentEl) return;

      const price = Number(priceCents) || 0;
      const compareAt = Number(compareAtCents) || 0;
      const compareDisplay = compareAt > price ? compareAt : 0;
      const isB2b = priceRoot.dataset.b2bCustomer === "true" ||
        !!priceRoot.querySelector("[data-b2b-customer]");
      const onSale = compareDisplay > 0 && !isB2b;

      currentEl.textContent = formatMoneyFn(price);
      currentWrap?.classList.toggle("price__current--sale", onSale);

      if (compareWrap && compareEl) {
        if (compareDisplay > 0) {
          compareEl.textContent = formatMoneyFn(compareDisplay);
          compareWrap.classList.add("price__compare--visible");
        } else {
          compareEl.textContent = "";
          compareWrap.classList.remove("price__compare--visible");
        }
      }
    };
    const getCurrentVariant = (section) => {
      const variantsJson = section.querySelector("[data-variants-json]");
      if (!variantsJson) return null;
      const variants = JSON.parse(variantsJson.textContent);
      const variantId = section.querySelector("[data-variant-id-input]")?.value;
      return variants.find((entry) => String(entry.id) === String(variantId)) || variants[0] || null;
    };
    const getAddonCompareAtTotal = (selection) => {
      if (!selection?.selectedOptions?.length) return 0;

      return selection.selectedOptions.reduce((sum, item) => {
        if (item.addonSlots?.length) {
          return (
            sum +
            item.addonSlots.reduce((slotSum, slot) => {
              const compare = Number(slot.compareAtPrice) || 0;
              const original = Number(slot.originalPrice) || 0;
              return slotSum + (compare > 0 ? compare : original);
            }, 0)
          );
        }

        const compare = Number(item.compareAtPrice) || 0;
        const original = Number(item.originalPrice) || 0;
        return sum + (compare > 0 ? compare : original);
      }, 0);
    };
    const applyPercentDiscount = (baseCents, percent) => {
      const value = Number(percent) || 0;
      if (value <= 0) return Number(baseCents) || 0;
      return Math.round((Number(baseCents) || 0) * (1 - value / 100));
    };
    const getMainProductDiscountContext = (selection) => {
      const discount = selection?.mainProductDiscount;
      if (!discount || !(Number(discount.discountPercent) > 0)) return null;
      return discount;
    };
    const resolveMainProductPricing = (section, submitBtn, selection, variant) => {
      const quantity = getQuantity(section);
      const isSubscribe = !!submitBtn?.classList.contains(SUBSCRIBE_CLASS);
      const baseUnitPrice = getProductUnitCents(section, submitBtn);
      const compareAtUnit =
        Number(variant?.compare_at_price) > 0
          ? Number(variant.compare_at_price)
          : Number(variant?.price) || 0;
      const regularUnit = Number(variant?.price) || baseUnitPrice;
      const discountContext = getMainProductDiscountContext(selection);
      let unitPrice = baseUnitPrice;
      let discountedUnit = baseUnitPrice;
      let discountPercent = 0;

      if (discountContext) {
        // Purchase-options tier map: percent depends on addon qty + once/subscribe.
        discountPercent = Number(discountContext.discountPercent) || 0;
        discountedUnit = applyPercentDiscount(compareAtUnit, discountPercent);
        unitPrice = discountedUnit;
      } else if (isSubscribe) {
        // Standard subscribe path (no tier override): use plan discount from the button.
        discountPercent = attrInt(submitBtn, "data-subscribe-discount");
        discountedUnit = baseUnitPrice;
        unitPrice = baseUnitPrice;
      } else if (compareAtUnit > regularUnit) {
        // Native compare-at sale when no tier/subscribe discount applies.
        discountPercent = computeCompareAtDiscount(compareAtUnit, regularUnit);
        discountedUnit = regularUnit;
        unitPrice = regularUnit;
      }

      return {
        quantity,
        unitPrice,
        discountedUnit,
        compareAtUnit,
        regularUnit,
        discountPercent,
        discountContext,
        productPrice: unitPrice * quantity,
        compareAtTotal: compareAtUnit * quantity
      };
    };
    const updateProductPriceDisplay = (section, variant, formatMoneyFn, selectionOverride) => {
      const priceRoot = section.querySelector("[data-product-price]");
      if (!priceRoot || !variant) return;

      storeProductPriceData(priceRoot, variant);

      const purchaseOptions = section.querySelector("purchase-options-component");
      const selection = selectionOverride || purchaseOptions?.selection || null;
      const submitBtn = section.querySelector("[data-submit-btn]");
      const mainPricing = resolveMainProductPricing(section, submitBtn, selection, variant);
      const priceCents = mainPricing.unitPrice;
      const compareAtCents =
        mainPricing.discountContext && mainPricing.compareAtUnit > priceCents
          ? mainPricing.compareAtUnit
          : Number(variant.compare_at_price) || 0;

      applyPriceDisplay(priceRoot, priceCents, compareAtCents, formatMoneyFn);
    };
    const syncProductPriceDisplay = (section, variant, formatMoneyFn, selectionOverride) => {
      const priceRoot = section.querySelector("[data-product-price]");
      const purchaseOptions = section.querySelector("purchase-options-component");
      const resolvedVariant = variant || getCurrentVariant(section);
      const selection = selectionOverride || purchaseOptions?.selection || null;
      const submitBtn = section.querySelector("[data-submit-btn]");
      const mainPricing = resolveMainProductPricing(section, submitBtn, selection, resolvedVariant);

      if (!isCombinedPriceEnabled(section)) {
        updateProductPriceDisplay(section, resolvedVariant, formatMoneyFn, selection);
        return;
      }

      if (!priceRoot || !resolvedVariant) {
        updateProductPriceDisplay(section, variant, formatMoneyFn, selection);
        return;
      }

      storeProductPriceData(priceRoot, resolvedVariant);

      const addonDiscountedTotal = Number(selection?.discountedPrice) || 0;

      if (!purchaseOptions || addonDiscountedTotal <= 0) {
        updateProductPriceDisplay(section, resolvedVariant, formatMoneyFn, selection);
        return;
      }

      const productCompareUnit =
        mainPricing.discountContext && mainPricing.compareAtUnit > 0
          ? mainPricing.compareAtUnit
          : Number(priceRoot.dataset.productCompareAtCents) > 0
            ? Number(priceRoot.dataset.productCompareAtCents)
            : Number(priceRoot.dataset.productPriceCents) || 0;
      const productCompareTotal = productCompareUnit * mainPricing.quantity;
      const addonCompareTotal = getAddonCompareAtTotal(selection);
      const combinedCompare = productCompareTotal + addonCompareTotal;
      const combinedPrice = mainPricing.productPrice + addonDiscountedTotal;

      if (combinedPrice <= 0) {
        updateProductPriceDisplay(section, resolvedVariant, formatMoneyFn, selection);
        return;
      }

      applyPriceDisplay(priceRoot, combinedPrice, combinedCompare, formatMoneyFn);
    };
    const renderAtcTemplate = (template, pricing, formatMoneyFn) => {
      let text = template || "Add To Cart - {total_price}";
      const discount = Math.max(0, Math.round(Number(pricing.discount) || 0));
      if (discount <= 0) {
        text = text.replace(/\s*\([^)]*\)/g, "").trimEnd();
      }
      return text
        .replace(/\{total_price\}/g, formatMoneyFn(pricing.totalPrice))
        .replace(/\{product_price\}/g, formatMoneyFn(pricing.productPrice))
        .replace(/\{discount\}/g, String(discount));
    };
    const getQuantity = (section) => {
      const qtyInput = section.querySelector('input[name="quantity"]');
      return Math.max(1, parseInt(qtyInput?.value || "1", 10));
    };
    const getProductUnitCents = (section, submitBtn) => {
      if (!submitBtn) return 0;
      const baseCents = attrInt(submitBtn, "data-base-price");
      const isSubscribe = submitBtn.classList.contains(SUBSCRIBE_CLASS);
      if (isSubscribe) {
        return attrInt(submitBtn, "data-subscribe-price") || getDiscountedPrice(
          baseCents,
          attrInt(submitBtn, "data-subscribe-discount")
        );
      }
      return baseCents;
    };
    const getAddonDiscountedTotal = (section, selectionOverride) => {
      if (selectionOverride != null) {
        return Number(selectionOverride.discountedPrice) || 0;
      }
      const purchaseOptions = section.querySelector("purchase-options-component");
      return Number(purchaseOptions?.selection?.discountedPrice) || 0;
    };
    const buildPurchasePanelPricing = (section, submitBtn, selectionOverride) => {
      const variant = getCurrentVariant(section);
      const selection =
        selectionOverride || section.querySelector("purchase-options-component")?.selection || null;
      const mainPricing = resolveMainProductPricing(section, submitBtn, selection, variant);
      const addonDiscountedPrice = getAddonDiscountedTotal(section, selectionOverride);
      const totalPrice = mainPricing.productPrice + addonDiscountedPrice;
      const discount = Math.max(0, Math.round(Number(mainPricing.discountPercent) || 0));

      return {
        productUnitPrice: mainPricing.unitPrice,
        productPrice: mainPricing.productPrice,
        addonDiscountedPrice,
        totalPrice,
        discount,
        quantity: mainPricing.quantity,
        mainProductCompareAtUnit: mainPricing.compareAtUnit,
        mainProductDiscountedUnit: mainPricing.discountedUnit,
        mainProductDiscountPercent: mainPricing.discountPercent,
        mainProductDiscount: mainPricing.discountContext
      };
    };
    const syncPurchasePanelPricing = (section, selectionOverride) => {
      const submitBtn = section.querySelector("[data-submit-btn]");
      const btnLabel = section.querySelector("[data-btn-label]");
      const btnPriceEl = section.querySelector("[data-btn-price]");
      const btnBadge = section.querySelector("[data-btn-badge]");
      const isSubscribe = !!submitBtn?.classList.contains(SUBSCRIBE_CLASS);
      const atcTemplate = submitBtn?.dataset.atcTextTemplate || "";
      const subscribeTemplate = submitBtn?.dataset.subscribeTextTemplate || "";
      const template = isSubscribe
        ? subscribeTemplate || atcTemplate
        : atcTemplate || subscribeTemplate;
      const templateMode = submitBtn?.dataset.atcTemplateMode === "true" && template;
      const pricing = buildPurchasePanelPricing(section, submitBtn, selectionOverride);

      section.purchasePanelPricing = pricing;
      section.dataset.purchasePanelPricing = JSON.stringify(pricing);

      syncProductPriceDisplay(section, getCurrentVariant(section), formatMoney, selectionOverride);

      if (templateMode && btnLabel) {
        btnLabel.textContent = renderAtcTemplate(template, pricing, formatMoney);
        if (btnPriceEl) btnPriceEl.hidden = true;
        if (btnBadge) btnBadge.hidden = true;
      } else if (btnPriceEl && submitBtn) {
        // Same total the button will actually charge, not just the main product's
        // own unit price — matches renderAtcTemplate's {total_price}, so a merchant
        // who blanks out the template text still gets a button price that includes
        // any active purchase-options add-on instead of duplicating a product-only calc.
        btnPriceEl.textContent = formatMoney(pricing.totalPrice);
      }

      section.dispatchEvent(
        new CustomEvent("purchase-panel:pricing-change", {
          bubbles: true,
          detail: { pricing }
        })
      );

      syncAvailability(section);

      return pricing;
    };
    const initPurchasePanelPricing = (section) => {
      const scheduleSync = (event) => {
        const selection = event?.detail?.selection || section.purchaseOptions?.selection || null;
        window.requestAnimationFrame(() => syncPurchasePanelPricing(section, selection));
      };

      section.addEventListener("purchase-options:change", scheduleSync);
      section.addEventListener("purchase-options:ready", scheduleSync);
      section.addEventListener("purchase-option:price-change", scheduleSync);
      section.addEventListener("quantity:change", scheduleSync);
      section.addEventListener("variant:change", scheduleSync);
      section.addEventListener("dropdown:change", scheduleSync);

      const purchaseToggle = section.querySelector("[data-purchase-toggle]");
      purchaseToggle?.addEventListener("click", () => {
        setTimeout(scheduleSync, 0);
      });

      const qtyInput = section.querySelector('input[name="quantity"]');
      qtyInput?.addEventListener("change", scheduleSync);

      scheduleSync();
    };
    const syncAvailability = (section) => {
      const submitBtn = section.querySelector("[data-submit-btn]");
      if (!submitBtn) return;
      const variant = getCurrentVariant(section);
      const available = !!(variant && variant.available);
      const btnLabel = section.querySelector("[data-btn-label]");
      const btnPriceEl = section.querySelector("[data-btn-price]");
      const btnBadge = section.querySelector("[data-btn-badge]");
      const templateMode = submitBtn.dataset.atcTemplateMode === "true";

      submitBtn.disabled = !available;
      submitBtn.setAttribute("aria-disabled", String(!available));
      submitBtn.classList.toggle("product-purchase-panel__btn--sold-out", !available);

      if (!available) {
        if (btnLabel) {
          if (!templateMode && submitBtn.dataset.origLabel == null) {
            submitBtn.dataset.origLabel = btnLabel.textContent;
          }
          btnLabel.textContent = submitBtn.dataset.soldOutText || "Sold out";
        }
        if (btnPriceEl) btnPriceEl.hidden = true;
        if (btnBadge) btnBadge.hidden = true;
      } else {
        if (!templateMode && btnLabel && submitBtn.dataset.origLabel != null) {
          btnLabel.textContent = submitBtn.dataset.origLabel;
          delete submitBtn.dataset.origLabel;
        }
        if (btnPriceEl) btnPriceEl.hidden = templateMode;
      }
    };
    const initMultiOptionPickers = (section, ctx) => {
      const {
        variants,
        variantIdInput,
        variantImage,
        submitBtn,
        btnPriceEl,
        animateImageTransition
      } = ctx;
      const container = section.querySelector("[data-option-levels]");
      if (!container) return false;
      const pickers = Array.from(
        container.querySelectorAll("[data-option-picker]")
      );
      if (!pickers.length) return false;
      const pickerInfo = pickers.map((picker) => ({
        picker,
        index: attrInt(picker, "data-option-position") - 1
      }));
      const eq = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
      const getSelectedValue = (picker) => {
        const sel = picker.querySelector(
          '[data-tab-value]:not(.button--unselected)'
        );
        return sel ? sel.getAttribute("data-tab-value") : null;
      };
      const setPickerSelected = (picker, value) => {
        picker.querySelectorAll("[data-tab-value]").forEach((b) => {
          const active = eq(b.getAttribute("data-tab-value"), value);
          b.classList.toggle("button--unselected", !active);
          b.setAttribute("aria-selected", String(active));
        });
        const selectedValue = picker
          .closest(".product-purchase-panel__option-level")
          ?.querySelector("[data-selected-option-value]");
        if (selectedValue) selectedValue.textContent = value;
      };
      const getAllowedValues = (index, selectedOptions) => {
        const allowed = new Set();
        variants.forEach((v) => {
          for (let j = 0; j < index; j++) {
            if (!eq(v.options[j], selectedOptions[j])) return;
          }
          allowed.add(String(v.options[index]).toLowerCase());
        });
        return allowed;
      };
      const updateDependentLevels = (selectedOptions) => {
        pickerInfo.forEach(({ picker, index }) => {
          if (index <= 0) return;
          const allowed = getAllowedValues(index, selectedOptions);
          picker.querySelectorAll("[data-tab-value]").forEach((b) => {
            b.hidden = !allowed.has(
              String(b.getAttribute("data-tab-value")).toLowerCase()
            );
          });
        });
      };
      const resolveVariant = (desired, changedIndex) => {
        const exact = variants.find((v) =>
          desired.every((val, i) => val == null || eq(v.options[i], val))
        );
        if (exact) return exact;
        const availableExact = variants.find(
          (v) =>
            v.available &&
            eq(v.options[changedIndex], desired[changedIndex])
        );
        if (availableExact) return availableExact;
        const candidates = variants.filter((v) =>
          eq(v.options[changedIndex], desired[changedIndex])
        );
        return candidates.find((v) => v.available) || candidates[0] || null;
      };
      const applyVariant = (matched, goRight) => {
        if (!matched) return;
        pickerInfo.forEach(({ picker, index }) =>
          setPickerSelected(picker, matched.options[index])
        );
        if (variantIdInput) variantIdInput.value = matched.id;
        if (submitBtn) submitBtn.setAttribute("data-base-price", matched.price);
        if (btnPriceEl && submitBtn?.dataset.atcTemplateMode !== "true")
          btnPriceEl.textContent = formatMoney(matched.price);
        syncCompareAtInputs(section, matched.compare_at_price || 0, matched.price);
        syncProductPriceDisplay(section, matched, formatMoney);
        section.dispatchEvent(
          new CustomEvent("variant:change", {
            detail: { variantId: String(matched.id) },
            bubbles: true
          })
        );
        if (variantImage && matched.featured_image?.src) {
          const newSrc = matched.featured_image.src.replace(
            /(\.[a-z]+)(\?|$)/,
            "_720x720$1$2"
          );
          animateImageTransition(
            newSrc,
            matched.featured_image.alt || "",
            goRight
          );
        }
        updateDependentLevels(matched.options);
        const url = new URL(window.location.href);
        url.searchParams.set("variant", matched.id);
        window.history.replaceState({}, "", url.toString());
        syncPurchasePanelPricing(section);
      };
      container.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-tab-value]");
        if (!btn) return;
        const picker = btn.closest("[data-option-picker]");
        if (!picker) return;
        const info = pickerInfo.find((p) => p.picker === picker);
        if (!info) return;
        const clickedValue = btn.getAttribute("data-tab-value");
        const siblings = Array.from(picker.querySelectorAll("[data-tab-value]"));
        const prevIdx = siblings.findIndex(
          (b) => !b.classList.contains("button--unselected")
        );
        const newIdx = siblings.indexOf(btn);
        const goRight = newIdx > prevIdx;
        const desired = [];
        pickerInfo.forEach(({ picker: p, index }) => {
          desired[index] = getSelectedValue(p);
        });
        desired[info.index] = clickedValue;
        const matched = resolveVariant(desired, info.index);
        applyVariant(matched, goRight);
      });
      const initialVariant =
        variants.find((v) => String(v.id) === String(variantIdInput?.value)) ||
        variants[0];
      if (initialVariant) updateDependentLevels(initialVariant.options);
      return true;
    };
    const initCombinedVariantPicker = (section, ctx) => {
      const {
        variants,
        variantIdInput,
        variantImage,
        submitBtn,
        btnPriceEl,
        animateImageTransition
      } = ctx;
      const picker = section.querySelector("[data-variant-combined]");
      if (!picker) return false;
      const applyVariantOrder = () => {
        const config = (picker.dataset.variantOrder || "").trim();
        if (!config) return;
        const buttons = Array.from(picker.querySelectorAll("[data-tab-value]"));
        if (!buttons.length) return;
        const normalize = (s) => String(s).trim().toLowerCase();
        const btnByTitle = new Map(
          buttons.map((b) => [normalize(b.getAttribute("data-variant-title")), b])
        );
        const pins = [];
        config.split(",").forEach((entry) => {
          const raw = entry.trim();
          const sep = raw.lastIndexOf("|");
          if (sep === -1) return;
          const title = normalize(raw.slice(0, sep));
          const place = parseInt(raw.slice(sep + 1).trim(), 10);
          if (title && btnByTitle.has(title) && Number.isFinite(place)) {
            pins.push({ title, place });
          }
        });
        if (!pins.length) return;
        const pinnedTitles = new Set(pins.map((p) => p.title));
        const unpinned = buttons.filter(
          (b) => !pinnedTitles.has(normalize(b.getAttribute("data-variant-title")))
        );
        const n = buttons.length;
        const result = new Array(n).fill(null);
        pins.sort((a, b) => a.place - b.place);
        pins.forEach((p) => {
          let idx = Math.min(Math.max(p.place - 1, 0), n - 1);
          let guard = 0;
          while (result[idx] != null && guard < n) {
            idx = (idx + 1) % n;
            guard += 1;
          }
          result[idx] = btnByTitle.get(p.title);
        });
        let u = 0;
        for (let i = 0; i < n; i++) {
          if (result[i] == null && u < unpinned.length) result[i] = unpinned[u++];
        }
        result.forEach((b) => {
          if (b) picker.appendChild(b);
        });
      };
      applyVariantOrder();
      const optionBtns = Array.from(picker.querySelectorAll("[data-tab-value]"));
      if (!optionBtns.length) return false;
      let currentPos = optionBtns.findIndex(
        (b) => !b.classList.contains("button--unselected")
      );
      picker.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-tab-value]");
        if (!btn) return;
        const variantId = btn.getAttribute("data-tab-value");
        const matched = variants.find(
          (v) => String(v.id) === String(variantId)
        );
        if (!matched) return;
        const newPos = optionBtns.indexOf(btn);
        const goRight = newPos > currentPos;
        currentPos = newPos;
        optionBtns.forEach((b) => {
          const active = b === btn;
          b.classList.toggle("button--unselected", !active);
          b.setAttribute("aria-selected", String(active));
        });
        if (variantIdInput) variantIdInput.value = matched.id;
        if (submitBtn) submitBtn.setAttribute("data-base-price", matched.price);
        if (btnPriceEl && submitBtn?.dataset.atcTemplateMode !== "true")
          btnPriceEl.textContent = formatMoney(matched.price);
        syncCompareAtInputs(section, matched.compare_at_price || 0, matched.price);
        syncProductPriceDisplay(section, matched, formatMoney);
        section.dispatchEvent(
          new CustomEvent("variant:change", {
            detail: { variantId: String(matched.id) },
            bubbles: true
          })
        );
        if (variantImage && matched.featured_image?.src) {
          const newSrc = matched.featured_image.src.replace(
            /(\.[a-z]+)(\?|$)/,
            "_720x720$1$2"
          );
          animateImageTransition(
            newSrc,
            matched.featured_image.alt || "",
            goRight
          );
        }
        const url = new URL(window.location.href);
        url.searchParams.set("variant", matched.id);
        window.history.replaceState({}, "", url.toString());
        syncPurchasePanelPricing(section);
      });
      return true;
    };
    const initPurchasePanel = (section) => {
      const variantsJson = section.querySelector("[data-variants-json]");
      if (!variantsJson) return;
      const variants = JSON.parse(variantsJson.textContent);
      const variantIdInput = section.querySelector("[data-variant-id-input]");
      const variantImage = section.querySelector("[data-variant-image]");
      const sizePicker = section.querySelector('[data-option-picker="size"]');
      const purchaseToggle = section.querySelector("[data-purchase-toggle]");
      const togglePill = section.querySelector("[data-toggle-pill]");
      const subscribeDetails = section.querySelector("[data-subscribe-details]");
      const submitBtn = section.querySelector("[data-submit-btn]");
      const btnInner = section.querySelector("[data-btn-inner]");
      const btnLabel = section.querySelector("[data-btn-label]");
      const btnPriceEl = section.querySelector("[data-btn-price]");
      const btnBadge = section.querySelector("[data-btn-badge]");
      const sellingPlanInput = section.querySelector("[data-selling-plan-input]");
      const animateImageTransition = makeAnimateImageTransition(
        variantImage,
        ANIM_PREFIX
      );
      const getActiveDiscount = () => (submitBtn == null ? void 0 : submitBtn.classList.contains(SUBSCRIBE_CLASS)) ? attrInt(submitBtn, "data-subscribe-discount") : 0;
      if (sizePicker) {
        const optionBtns = Array.from(
          sizePicker.querySelectorAll("[data-tab-value]")
        );
        let currentOptionPos = optionBtns.findIndex(
          (b) => !b.classList.contains("button--unselected")
        );
        sizePicker.addEventListener("click", (e) => {
          var _a;
          const btn = e.target.closest("[data-tab-value]");
          if (!btn) return;
          const selectedValue = btn.getAttribute("data-tab-value");
          // Match against ANY of the variant's option values, not just the one at this
          // picker's own option position. This keeps existing single-option products
          // working exactly as before, while also supporting merged rows (e.g. oils that
          // fold a separate Pro-Pod "Type" option into the same row as Size) where a
          // button's value — like "Pro-Pod™" — lives at a different option position
          // than the Size values.
          const matched = variants.find(
            (v) => v.options.some((optValue) => String(optValue).toLowerCase() === String(selectedValue).toLowerCase())
          );
          if (!matched) return;
          const newPos = optionBtns.indexOf(btn);
          const goRight = newPos > currentOptionPos;
          currentOptionPos = newPos;
          sizePicker.querySelectorAll("[data-tab-value]").forEach((b) => {
            const active = b.getAttribute("data-tab-value") === selectedValue;
            b.classList.toggle("button--unselected", !active);
            b.setAttribute("aria-selected", String(active));
          });
          if (variantIdInput)
            variantIdInput.value = matched.id;
          if (submitBtn) submitBtn.setAttribute("data-base-price", matched.price);
          if (btnPriceEl && submitBtn?.dataset.atcTemplateMode !== "true")
            btnPriceEl.textContent = formatMoney(matched.price);
          syncCompareAtInputs(
            section,
            matched.compare_at_price || 0,
            matched.price
          );
          syncProductPriceDisplay(section, matched, formatMoney);
          section.dispatchEvent(
            new CustomEvent("variant:change", {
              detail: { variantId: String(matched.id) },
              bubbles: true
            })
          );
          if (variantImage && ((_a = matched.featured_image) == null ? void 0 : _a.src) && optionBtns.length > 1) {
            const newSrc = matched.featured_image.src.replace(
              /(\.[a-z]+)(\?|$)/,
              "_720x720$1$2"
            );
            animateImageTransition(
              newSrc,
              matched.featured_image.alt || "",
              goRight
            );
          }
          const url = new URL(window.location.href);
          url.searchParams.set("variant", matched.id);
          window.history.replaceState({}, "", url.toString());
          syncPurchasePanelPricing(section);
        });
      }
      initMultiOptionPickers(section, {
        variants,
        variantIdInput,
        variantImage,
        submitBtn,
        btnPriceEl,
        animateImageTransition
      });
      initCombinedVariantPicker(section, {
        variants,
        variantIdInput,
        variantImage,
        submitBtn,
        btnPriceEl,
        animateImageTransition
      });
      initQuantityChange(section, {
        submitBtn,
        btnPriceEl,
        subscribeClass: SUBSCRIBE_CLASS,
        qtyInput: section.querySelector('input[name="quantity"]')
      });
      initSubscribeToggle(section, {
        submitBtn,
        btnInner,
        btnLabel,
        btnPriceEl,
        btnBadge,
        sellingPlanInput,
        purchaseToggle,
        togglePill,
        subscribeDetails,
        subscribeClass: SUBSCRIBE_CLASS,
        addToCartClass: ADD_TO_CART_CLASS,
        toggleBtnActiveClass: TOGGLE_BTN_ACTIVE_CLASS,
        pillRightClass: PILL_RIGHT_CLASS,
        collapsibleHiddenClass: COLLAPSIBLE_HIDDEN_CLASS,
        animPrefix: ANIM_PREFIX
      });
      initVariantPlanSync(section, {
        sellingPlanInput,
        variantIdInput,
        submitBtn,
        btnPriceEl,
        btnBadge,
        purchaseToggle,
        frequencyWrap: section.querySelector("[data-subscription-frequency]"),
        subscribeClass: SUBSCRIBE_CLASS
      });
      if (new URLSearchParams(window.location.search).has("from_subscription_management") && purchaseToggle) {
        const subscribeBtn = purchaseToggle.querySelector('[data-toggle="subscribe"]');
        subscribeBtn == null ? void 0 : subscribeBtn.click();
        purchaseToggle.hidden = true;
      }
      initPurchaseOptions(section);
      initReadMore(section);
      initPurchasePanelPricing(section);
      syncProductPriceDisplay(section, getCurrentVariant(section), formatMoney);
      section.addEventListener("variant:change", () => syncAvailability(section));
      syncAvailability(section);
      initAddToCart(section);
    };
    const initAddToCart = (section) => {
      const form = section.querySelector("form");
      if (!form) return;

      const purchaseOptions = section.querySelector("purchase-options-component");

      const feedbackEl = section.querySelector("[data-atc-feedback]");
      const submitBtn = section.querySelector("[data-submit-btn]");
      const successMessage = section.dataset.atcSuccess || "Added to cart";
      const errorMessage = section.dataset.atcError || "Could not add to cart. Please try again.";
      const validationMessage = section.dataset.atcValidationError || "Please complete your selections before adding to cart.";
      let feedbackTimer = null;

      const showFeedback = (message, type) => {
        if (!feedbackEl || !message) return;
        feedbackEl.textContent = message;
        feedbackEl.hidden = false;
        feedbackEl.classList.toggle("product-purchase-panel__atc-feedback--success", type === "success");
        feedbackEl.classList.toggle("product-purchase-panel__atc-feedback--error", type === "error");
        if (feedbackTimer) clearTimeout(feedbackTimer);
        feedbackTimer = setTimeout(() => {
          feedbackEl.hidden = true;
        }, 4000);
      };

      const buildCartItems = () => {
        const formData = new FormData(form);
        const mainVariantId = formData.get("id");
        if (!mainVariantId) return null;

        const quantity = Math.max(1, parseInt(formData.get("quantity") || "1", 10));
        const sellingPlanInput = section.querySelector("[data-selling-plan-input]");
        const isSubscribeMode = !!submitBtn?.classList.contains(SUBSCRIBE_CLASS);
        // Prefer the live form value; fall back to the dataset id while in subscribe mode
        // so a cleared/stale input still attaches the selected plan.
        const sellingPlan =
          formData.get("selling_plan") ||
          (isSubscribeMode
            ? sellingPlanInput?.value || sellingPlanInput?.dataset.sellingPlanId || ""
            : "");
        const quarterlyPlanId = String(section.dataset.quarterlySellingPlanId || "").trim();
        const isQuarterlyPlan =
          !!sellingPlan && !!quarterlyPlanId && String(sellingPlan) === quarterlyPlanId;
        const hasAddons = Boolean(purchaseOptions?.selectedOptions?.length);
        const bundleId = hasAddons ? generateBundleId() : null;
        const selection = purchaseOptions?.selection || null;
        const variant = getCurrentVariant(section);
        const mainPricing = resolveMainProductPricing(section, submitBtn, selection, variant);
        const promotionalSource = String(selection?.promotionalSource || "").trim();
        const mainProperties = {
          // Always native variant price vs compare-at — never the post-discount unit.
          ...buildCompareAtProperties(mainPricing.compareAtUnit, mainPricing.regularUnit),
          ...(bundleId ? { _bundle_id: bundleId } : {}),
          ...(isQuarterlyPlan ? { _quaterly_subs_key: "true" } : {}),
          ...(promotionalSource ? { _promotional_source: promotionalSource } : {})
        };
        const items = [
          {
            id: Number(mainVariantId),
            quantity: isQuarterlyPlan ? 3 : quantity,
            ...(sellingPlan ? { selling_plan: Number(sellingPlan) } : {}),
            properties: mainProperties
          }
        ];

        if (hasAddons) {
          items.push(...purchaseOptions.buildAddonCartLines(bundleId));
        }

        return items;
      };

      form.addEventListener(
        "submit",
        async (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();

          const validation = purchaseOptions?.validateForCart() ?? { ok: true };
          if (!validation.ok) {
            showFeedback(validation.message || validationMessage, "error");
            return;
          }

          const items = buildCartItems();
          if (!items?.length || !items[0].id) {
            showFeedback(errorMessage, "error");
            return;
          }

          if (submitBtn) submitBtn.disabled = true;

          try {
            const response = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json"
              },
              body: JSON.stringify({ items })
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
              throw new Error(data.description || data.message || errorMessage);
            }

            showFeedback(successMessage, "success");
            await window.HotelCollection?.refreshCart?.();
            window.HotelCollection?.openCart?.();
            document.dispatchEvent(
              new CustomEvent("cart:item-added", {
                detail: {
                  variantId: String(items[0].id),
                  isUpsell: false
                }
              })
            );
          } catch (error) {
            showFeedback(error?.message || errorMessage, "error");
          } finally {
            if (submitBtn) submitBtn.disabled = false;
          }
        },
        true
      );
    };
    const syncMainPurchaseControls = (section, selectionOverride) => {
      const purchaseOptions = section.querySelector("purchase-options-component");
      const hide =
        selectionOverride?.hasSelectedFormOption ??
        purchaseOptions?.hasSelectedFormOption ??
        false;

      section.querySelector("[data-purchase-panel-stepper]")?.toggleAttribute("hidden", hide);
      section.querySelector("[data-purchase-panel-ctas]")?.toggleAttribute("hidden", hide);
      section.querySelector("[data-purchase-panel-express]")?.toggleAttribute("hidden", hide);
    };
    const initPurchaseOptions = (section) => {
      const purchaseOptions = section.querySelector("purchase-options-component");
      if (!purchaseOptions) return;
      section.purchaseOptions = purchaseOptions;

      const updateSelection = (event) => {
        const pricing = buildPurchasePanelPricing(
          section,
          section.querySelector("[data-submit-btn]"),
          event.detail.selection
        );
        const selection = {
          ...event.detail.selection,
          productPrice: pricing.productPrice,
          addonDiscountedPrice: pricing.addonDiscountedPrice,
          totalPrice: pricing.totalPrice,
          discount: pricing.discount,
          quantity: pricing.quantity,
          mainProductCompareAtUnit: pricing.mainProductCompareAtUnit,
          mainProductDiscountedUnit: pricing.mainProductDiscountedUnit,
          mainProductDiscountPercent: pricing.mainProductDiscountPercent,
          mainProductDiscount: pricing.mainProductDiscount
        };

        section.purchaseOptionsSelection = {
          ...event.detail,
          selection
        };
        section.dataset.purchaseOptionsSelection = JSON.stringify(selection);
        section.purchasePanelPricing = pricing;
        section.dataset.purchasePanelPricing = JSON.stringify(pricing);
        syncMainPurchaseControls(section, selection);
      };

      purchaseOptions.addEventListener("purchase-options:change", updateSelection);
      purchaseOptions.addEventListener("purchase-options:ready", updateSelection);
      syncMainPurchaseControls(section, purchaseOptions.selection);
    };
    const initReadMore = (section) => {
      const wrap = section.querySelector("[data-readmore]");
      if (!wrap) return;
      const toggle = wrap.querySelector("[data-readmore-toggle]");
      if (!toggle) return;
      const label = wrap.querySelector("[data-readmore-label]");
      toggle.addEventListener("click", () => {
        const expanded = wrap.getAttribute("data-expanded") === "true";
        const next = !expanded;
        wrap.setAttribute("data-expanded", String(next));
        toggle.setAttribute("aria-expanded", String(next));
        if (label) label.textContent = next ? "Read less" : "Read more";
      });
    };
    document.querySelectorAll(".product-purchase-panel").forEach(initPurchasePanel);
  })();
})();
